'use client';

import { type UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AccessState, AuthenticatedUser } from '@westmont/contracts';
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { forgetPostLoginRedirect, LOGIN_PATH } from '@/lib/auth-routes';
import { clearFilterScope } from '@/lib/filter-scope-storage';
import { ACCESS_STATE_QUERY_KEY, SESSION_QUERY_KEY } from '@/lib/query-keys';

/** Re-exported so existing importers keep their path; owned by `query-keys.ts`. */
export { ACCESS_STATE_QUERY_KEY, SESSION_QUERY_KEY };

/**
 * E04 US-17 — session bootstrap for authenticated pages.
 *
 * This hook READS the session; it never routes. Every auth redirect in the app
 * belongs to AuthBoundary (see `lib/auth-routes.ts` for why). A page calls this
 * for the signed-in principal — the boundary above it has already guaranteed
 * there is one.
 *
 * AC2 (sliding 30' timeout): the BE answers 401 "Session expired" once
 * `expires_at` passes; this hook re-checks the session exactly when the
 * last-known `session_expires_at` elapses (idle tab included). The boundary
 * turns that 401 into the bounce to /login (same contract as sign-out,
 * US-19 AC3).
 *
 * The pre-expiry warning toast ("còn 2 phút") from the spec's UI notes has
 * no Figma frame yet — deferred, see status.md Defer Log.
 */

export type SessionData = { user: AuthenticatedUser; session_expires_at: string };

/**
 * Shared options for the session query, so every caller joins the SAME cache
 * entry with the same fetch behaviour instead of triggering its own refetch.
 */
export function sessionQueryOptions(): UseQueryOptions<SessionData, Error> {
  return {
    queryKey: SESSION_QUERY_KEY,
    retry: false,
    queryFn: async () => {
      const res = await api.authSessions.me();
      if (res.status !== 200) throw new Error(res.body.message);
      return res.body;
    },
  };
}

/** Shared options for the tenant access state (US-04). */
export function accessStateQueryOptions(enabled: boolean): UseQueryOptions<AccessState, Error> {
  return {
    queryKey: ACCESS_STATE_QUERY_KEY,
    enabled,
    retry: false,
    queryFn: async () => {
      const res = await api.organizations.accessState();
      if (res.status !== 200) throw new Error(res.body.message);
      return res.body;
    },
  };
}

export function useAuthSession() {
  const session = useQuery(sessionQueryOptions());

  const expiresAt = session.data?.session_expires_at;
  const { refetch } = session;

  useEffect(() => {
    if (!expiresAt) return;
    // +1s so the BE clock has definitely crossed expires_at when we ask.
    const delay = Math.max(0, new Date(expiresAt).getTime() - Date.now() + 1000);
    const timer = setTimeout(() => refetch(), delay);
    return () => clearTimeout(timer);
  }, [expiresAt, refetch]);

  return session;
}

/** The tenant lifecycle state, shared with AuthBoundary's cache entry. */
export function useAccessState(enabled: boolean) {
  return useQuery(accessStateQueryOptions(enabled));
}

/**
 * End the session locally: forget the post-login target, drop the shared header
 * filters and drop every cached response. Used by sign-out and by the boundary
 * when the BE rejects the session, so a stale target or another tenant's data
 * can never be read back by whoever signs in next (localStorage, sessionStorage
 * and the query cache all outlive a session; the departure is a same-tab
 * `location.assign`). Since 2026-08-20 the cookie is named per host, so this
 * clears the session of THIS host only: a second workspace signed in on another
 * subdomain of the same browser stays signed in, which is the point of naming
 * it per host.
 *
 * `clearFilterScope` is belt and braces: the stored trio carries the
 * organization and user it was written for and `readFilterScope` already
 * refuses to hand it to anyone else. Dropping it here means it is not sitting
 * in the tab at all between two sign-ins.
 */
export function useEndSession() {
  const queryClient = useQueryClient();
  // Stable across renders: callers put it in effect deps, and an identity that
  // changed every render would re-run those effects forever.
  return useCallback(() => {
    forgetPostLoginRedirect();
    clearFilterScope();
    queryClient.clear();
  }, [queryClient]);
}

/**
 * The ONE sign-out. `useEndSession` clears the local half (stored target +
 * query cache); this adds the BE call and the departure, so no screen has to
 * reassemble the sequence by hand.
 *
 * A rejected logout still leaves. A screen whose only exit can fail closed is
 * the dead end all over again (BA 18/08: document submission had no exit at
 * all), so the BE error is swallowed on purpose.
 *
 * The departure is a full page load, not `router.replace`: it drops every
 * in-flight query along with the cookie, and that is what lets a public route
 * such as /signup render instead of being bounced by rule 4 of `resolveRoute`,
 * which only redirects an AUTHENTICATED visitor.
 */
export function useSignOut(destination: string = LOGIN_PATH) {
  const endSession = useEndSession();
  const [pending, setPending] = useState(false);

  const signOut = useCallback(async () => {
    // Repeated clicks fired several logouts before the redirect landed.
    if (pending) return;
    setPending(true);
    try {
      await api.authSessions.logout();
    } catch {
      // Leaving anyway: see above.
    }
    endSession();
    window.location.assign(destination);
  }, [pending, endSession, destination]);

  return { signOut, pending };
}
